import React, { useState, useMemo, useEffect } from 'react';

import { GlobeFilled, ReportBarChartFilled } from '@tag/tag-icons';

import { UpliftRegion, UpliftIndex, UpliftIndexTypePascalCaseEnum } from '@/graphql/contracts/_generated_/contracts';
import { Dropdown } from '@/components/common/Dropdown/Dropdown';
import TagTabs, { TabType } from '@/components/common/TagTabs/TagTabs';
import PageTitle from '@/components/common/PageTitle/PageTitle';
import Page from '@/components/Page/Page';
import { GraphTab } from './components/GraphTab';
import { TableTab } from './components/TableTab';
import { useGetUpliftRegions } from './hooks/useGetUpliftRegions';
import { useGetUpliftIndexes } from './hooks/useGetUpliftIndexes';
import styles from './uplift-rates.module.scss';

import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';

import AddUpliftIndexModal from './components/AddUpliftIndexModal';
import RenameUpliftIndexModal from './components/RenameUpliftIndexModal';
import DeleteUpliftIndexModal from './components/DeleteUpliftIndexModal';
import RateModal from './components/RateModal';
import { useAddUpliftRatesToIndex } from './hooks/useAddUpliftRatesToIndex';
import { GetUpliftIndexesVariables } from '@/graphql/contracts/_generated_/getUpliftIndexes.query';

const UpliftRates: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState<UpliftRegion | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<UpliftIndex | null>(null);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddRateOpen, setIsAddRateOpen] = useState(false);

  const { regions, loading: regionsLoading } = useGetUpliftRegions();
  const { getUpliftIndexes, indexes, loading: indexesLoading } = useGetUpliftIndexes();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();
  const isAdminOrFullAccessRole = userRole === UserRole.ADMIN || userRole === UserRole.FULL_ACCESS;
  
  const addRateRefetchVars = useMemo(() => ({
    region: selectedRegion?.name,
    includeRates: true,
  }), [selectedRegion]);
  const [addRatesToIndex] = useAddUpliftRatesToIndex(addRateRefetchVars as GetUpliftIndexesVariables);

  useEffect(() => {
    getUserRole().then(setUserRole);
  }, [getUserRole]);

  const regionOptions = useMemo(() => 
    regions.map(region => ({
      value: region.name,
      label: region.name,
      data: region
    })), [regions]);

  const indexOptions = useMemo(() => {
    return indexes.map(index => ({
      value: index.id,
      label: `${index.name} (${index.type === UpliftIndexTypePascalCaseEnum.Published ? 'published' : 'custom'})`,
      data: index
    }));
  }, [indexes]);

  const typeOptions = useMemo(() => ([
    { value: UpliftIndexTypePascalCaseEnum.Published, label: 'Published' },
    { value: UpliftIndexTypePascalCaseEnum.Custom, label: 'Custom' },
  ]), []);

  const handleRegionChange = (option: any) => {
    setSelectedRegion(option?.data || null);
    setSelectedIndex(null);
  };

  const handleIndexChange = (option: any) => {
    setSelectedIndex(option?.data || null);
  };

  useEffect(() => {
    if (selectedRegion) {
      getUpliftIndexes({ 
        region: selectedRegion.name, 
        includeRates: true 
      });
    }
  }, [selectedRegion, getUpliftIndexes]);

  useEffect(() => {
    if (selectedIndex && indexes?.length) {
      const updated = indexes.find(i => i.id === selectedIndex.id);
      if (updated) {
        setSelectedIndex(updated as UpliftIndex);
      }
    }
  }, [indexes]);

  const tabs: TabType[] = [
    {
      title: 'Graph',
      name: 'GRAPH',
      ContentComponent: () => <GraphTab selectedIndex={selectedIndex} />
    },
    {
      title: 'Table',
      name: 'TABLE',
      ContentComponent: () => (
        <TableTab
          selectedIndex={selectedIndex}
          isAdminOrFullAccessRole={isAdminOrFullAccessRole}
          regionName={selectedRegion?.name || null}
        />
      )
    }
  ];

  const dataSources = useMemo(() => ({
    'UK_CPI': 'https://www.ons.gov.uk/economy/inflationandpriceindices/timeseries/d7g7/mm23',
    'US_CPI': 'https://www.bls.gov/news.release/cpi.nr0.htm',
  }), []);

  const dataSource = useMemo(() => {
    if (selectedRegion?.name && selectedIndex?.name) {
      const key = `${selectedRegion.name}_${selectedIndex.name}`;
      return dataSources[key as keyof typeof dataSources];
    }
    return null;
  }, [selectedRegion, selectedIndex, dataSources]);

  return (
    <Page
      padding={true}
      pageTitle="Uplift Rates"
    >
      <div className={styles.upliftRatesPage}>
        <div className={styles.pageTitle}>
          <PageTitle title="Uplift Rates" />
        </div>
        <div className={styles.filtersSection}>
          <div className={styles.filterGroup}>
            <div className={styles.filterLabel}>
              <GlobeFilled accent="blue" />
              <span>Region</span>
            </div>
            <Dropdown
              options={regionOptions}
              placeholder={regionsLoading ? 'Loading regions...' : 'Select a region...'}
              onChange={handleRegionChange}
              value={selectedRegion ? {
                value: selectedRegion.name,
                label: selectedRegion.name,
                data: selectedRegion
              } : null}
              isClearable={false}
              needToShowInputTextOnBlur={true}
              isDisabled={regionsLoading}
              className={styles.regionDropdown}
            />
          </div>

          <div className={styles.filterGroup}>
            <div className={styles.filterLabel}>
              <ReportBarChartFilled accent="green" />
              <span>Index</span>
              {isAdminOrFullAccessRole && selectedRegion && (
                <a
                  onClick={() => setIsAddOpen(true)}
                  className={styles.indexActionLink}
                >
                  Add
                </a>
              )}
              {isAdminOrFullAccessRole && selectedIndex && (
                <>
                  {selectedIndex.type === UpliftIndexTypePascalCaseEnum.Published ? (
                    <span className={styles.indexActionLinkDisabled}>Rename</span>
                  ) : (
                    <a
                      onClick={() => setIsRenameOpen(true)}
                      className={styles.indexActionLink}
                    >
                      Rename
                    </a>
                  )}
                  {selectedIndex.type === UpliftIndexTypePascalCaseEnum.Published ? (
                    <span className={styles.indexActionLinkDisabled}>
                      Delete
                    </span>
                  ) : (
                    <a
                      onClick={() => setIsDeleteOpen(true)}
                      className={styles.indexActionLink}
                    >
                      Delete
                    </a>
                  )}
                </>
              )}
            </div>
            <Dropdown
              options={indexOptions}
              placeholder={
                !selectedRegion ? 'Select a region first' :
                  indexesLoading ? 'Loading indexes...' :
                    'Select an index...'
              }
              onChange={handleIndexChange}
              value={selectedIndex ? {
                value: selectedIndex.id,
                label: `${selectedIndex.name} (${
                  selectedIndex.type === UpliftIndexTypePascalCaseEnum.Published ? 'published' : 'custom'
                })`,
                data: selectedIndex
              } : null}
              isDisabled={!selectedRegion || indexesLoading}
              isClearable={false}
              needToShowInputTextOnBlur={true}
              className={styles.indexDropdown}
            />
          </div>
        </div>

        <div className={styles.contentSection}>
          <div className={styles.tabsHeader}>
              <h2>📊 Index Rates</h2>
              {selectedIndex && (
                <div className={styles.selectedIndexInfo}>
                  <span className={styles.selectedRegion}>
                    🌍 {selectedRegion?.name}
                  </span>
                  <span className={styles.selectedIndex}>
                    📈 {selectedIndex.name}
                  </span>
                  <span className={styles.indexType}>
                    {selectedIndex.type === UpliftIndexTypePascalCaseEnum.Published ? '📢 Published' : '🔧 Custom'}
                  </span>
                  { dataSource && (
                    <a href={dataSource} target='_blank'>
                      Data Source
                    </a>
                  )}
                  {isAdminOrFullAccessRole && selectedIndex && 
                  selectedIndex.type === UpliftIndexTypePascalCaseEnum.Custom && (
                    <a
                      onClick={() => setIsAddRateOpen(true)}
                      className={styles.indexActionLink}
                      style={{ marginLeft: 'auto' }}
                    >
                      Add Rate
                    </a>
                  )}
                </div>
              )}
          </div>

          <TagTabs
            tabs={tabs}
            activeTabKey='GRAPH'
          />

        </div>
      </div>

      {isAddOpen && selectedRegion && (
        <AddUpliftIndexModal
          isOpen={true}
          onClose={() => setIsAddOpen(false)}
          selectedRegion={selectedRegion}
          regionOptions={regionOptions}
          typeOptions={typeOptions}
          onSuccess={(newIndex) => setSelectedIndex(newIndex)}
        />
      )}

      {isRenameOpen && selectedIndex && (
        <RenameUpliftIndexModal
          isOpen={true}
          onClose={() => setIsRenameOpen(false)}
          selectedIndex={selectedIndex}
          selectedRegionName={selectedRegion?.name}
          onSuccess={(updated) => setSelectedIndex(updated)}
        />
      )}

      {isAddRateOpen && selectedIndex && selectedRegion && (
        <RateModal
          isOpen={true}
          onClose={() => setIsAddRateOpen(false)}
          title="Add Rate"
          onSubmit={async ({ rate, effectiveDate }) => {
            await addRatesToIndex({
              variables: {
                input: {
                  upliftIndexId: selectedIndex.id!,
                  upliftIndexRates: [{
                    rate: rate / 100, // Convert back to decimal fraction
                    effectiveDate,
                  }],
                },
              },
            });
            setIsAddRateOpen(false);
          }}
        />
      )}

      {isDeleteOpen && selectedIndex && (
        <DeleteUpliftIndexModal
          isOpen={true}
          onClose={() => setIsDeleteOpen(false)}
          selectedIndex={selectedIndex}
          selectedRegionName={selectedRegion?.name}
          onDeleted={() => setSelectedIndex(null)}
        />
      )}
    </Page>
  );
};

export default UpliftRates;
